import { create } from 'zustand';

import DebugConsole from '@helper/functions/console';
import {
  BpointSetupPayerAuthResponse,
  processPayment as bpointProcessPayment,
  setupPayerAuth,
  updateTxnDetails,
  validateRecaptcha,
} from '@helper/service/bpoint';

import { useActiveTransactionStore } from './generic-payment';

export type BpointPaymentDetails = {
  message?: string;
  isToken: boolean;
  card: {
    number: string;
    expiry: {
      month: string;
      year: string;
    };
    scheme: string;
    localisation: string;
    type: string;
  };
  accepted: boolean;
  surcharge: number;
};

type State = {
  reset: () => void;

  authkey: string;
  setAuthkey: (authkey: string) => void;

  paymentDetails: BpointPaymentDetails;
  setPaymentDetails: (data: BpointPaymentDetails) => void;

  updateTxnDetails: (props: {
    healthEntityCode: string;
    paymentType: string;
    orderAmount: string;
    orderReference: string;
  }) => Promise<null | string>;

  validateRecaptcha: (token: string) => Promise<boolean>;

  payerAuthRes: BpointSetupPayerAuthResponse;
  setupPayerAuth: () => Promise<
    | false
    | {
        iframe: {
          url: 'string';
          origin: 'string';
        };
        authentication: {
          status: 'InProgress' | 'Complete';
        };
      }
  >;

  processPayment: (payload: Record<string, any>) => Promise<false | any>;
};

export const usePaymentBpointStore = create<State>((set, get) => ({
  reset: () => {
    set({
      authkey: '',
      paymentDetails: null,
      payerAuthRes: null,
    });
  },

  authkey: '',
  setAuthkey: (authkey) => {
    set({
      authkey,
    });
  },

  paymentDetails: null,
  setPaymentDetails: (data) => {
    set({
      paymentDetails: data,
    });
  },

  updateTxnDetails: async ({ healthEntityCode, paymentType, orderAmount, orderReference }) => {
    const authkey = get().authkey;

    if (!authkey) {
      return 'Undefined authkey';
    }

    await updateTxnDetails({
      authkey,
      healthEntityCode,
      paymentType,
      orderAmount,
      orderReference,
    });

    return null;
  },

  validateRecaptcha: async (token: string) => {
    const res = await validateRecaptcha(token);

    return Boolean(res);
  },

  payerAuthRes: null,
  setupPayerAuth: async () => {
    const authkey = get().authkey;
    if (!authkey) {
      DebugConsole.error(`Cannot setup payer auth. Missing authkey.`);
      return;
    }

    const activeTx = useActiveTransactionStore.getState().activeTx;
    if (!activeTx) {
      DebugConsole.error(`Cannot setup payer auth: invalid or missing active TX`);
      return;
    }

    const healthEntityCode = activeTx?.healthEntityCode;
    if (!healthEntityCode) {
      DebugConsole.error(`Cannot setup payer auth: invalid or missing healthEntityCode`);
      return;
    }

    const paymentType = activeTx?.paymentType;
    if (!paymentType) {
      DebugConsole.error(`Cannot setup payer auth: invalid or missing paymentType`);
      return;
    }

    const payerAuthRes = await setupPayerAuth({
      authkey,
      healthEntityCode,
      paymentType,
    });

    set({
      payerAuthRes: payerAuthRes || null,
    });

    return payerAuthRes;
  },

  processPayment: async (payload) => {
    const authkey = get().authkey;

    if (!authkey) {
      DebugConsole.error(`Cannot submit BPOINT payment. Missing authkey.`);
      return;
    }

    const paymentRes = await bpointProcessPayment({ ...payload, authkey });

    return paymentRes;
  },
}));
